import { useReadContracts } from 'wagmi'
import { ADDRESSES, isLiveAddr } from '@/lib/contracts'
import distributorAbi from '@/lib/abis/HorizonRevenueDistributor.json'
import { POLL_SLOW } from '@/lib/utils'

/// The revenue-share assets the HorizonRevenueDistributor pays veHORIZON holders (redesign entry 312:
/// the ve's old Synthetix-style streaming leg was replaced by a Curve-style per-week epoch
/// distributor). Unlike the old ve stream, the distributor keeps NO enumerable on-chain asset list -
/// `tokensPerWeek` is a bare mapping, exactly as Curve's FeeDistributor is single-asset - so the
/// canonical list is config-driven. WETH only at launch, because RevenueRouter settles every
/// non-vault revenue source (borrow fees, liquidation cuts, reserve interest) to WETH before it ever
/// reaches lockers (see RevenueRouter.sol's whitelist doc + HorizonRevenueDistributor.sol). Should the
/// router ever pay a second asset natively, adding it is one entry here - the same shape the old
/// EXPECTED_REVENUE_ASSETS list had. Zero-address entries (an unlisted asset on this chain) drop out.
export const REVENUE_ASSETS = [ADDRESSES.WETH].filter((a) => isLiveAddr(a))

const DISTRIBUTOR = ADDRESSES.HORIZON_REVENUE_DISTRIBUTOR

/// Read side of the ve revenue share, now sourced from HorizonRevenueDistributor. Reads this wallet's
/// `claimable(asset, user)` for each revenue asset. Shared by StakeSection's RevenueSharePanel (which
/// also claims via `claimMany`) and PortfolioSection's Claim-All sweep, so both agree on what "pending
/// revenue share" means instead of two independently-maintained queries drifting apart.
///
/// `claimable` is a view that reflects state as of the last on-chain checkpoint (it does not itself
/// crank `checkpointTotalSupply`), so it is an exact lower bound: a completed week only becomes visible
/// once its ve-supply snapshot has been filled, which the actual `claim` does internally and which the
/// protocol's own permissionless keeper cranks weekly. Displaying the lower bound is honest - a claim
/// pays at least what is shown, and never reverts on a shown amount.
export function useRevenueShare(user) {
  const enabled = isLiveAddr(DISTRIBUTOR)
  const assets = REVENUE_ASSETS

  const { data: pendingReads, refetch: refetchPending } = useReadContracts({
    query: { enabled: enabled && !!user && assets.length > 0, refetchInterval: POLL_SLOW },
    contracts: assets.map((asset) => ({
      address: DISTRIBUTOR, abi: distributorAbi, functionName: 'claimable', args: [asset, user],
    })),
  })
  const pendingByAsset = assets.map((asset, i) => ({ asset, amount: pendingReads?.[i]?.result ?? 0n }))
  const hasAnyClaimable = pendingByAsset.some((p) => p.amount > 0n)

  return { assets, pendingByAsset, hasAnyClaimable, refetchPending }
}
